/**
 * Standalone zoom controls hint overlay (quick view + PDP).
 * Dismisses only via "Got it".
 * deploy: 2026-06-11-zoom-hint-reliable
 */

const HINT_ID = 'ctc-zoom-controls-hint-overlay';
const LAYER_CLASS = 'ctc-zoom-hint-layer';
const STYLE_ID = 'ctc-zoom-controls-hint-styles';

/** @type {AbortController | null} */
let abortController = null;

/** @type {boolean} */
let dismissedForSession = false;

function isMobileViewport() {
  return !window.matchMedia('(min-width: 750px)').matches;
}

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .${LAYER_CLASS} {
      position: fixed;
      inset: 0;
      z-index: 2147483645;
      pointer-events: none;
    }

    #${HINT_ID}.ctc-zoom-controls-hint {
      --ctc-hint-accent: #6ec8ff;
      position: fixed;
      z-index: 2147483646;
      max-width: min(22rem, calc(100vw - 2rem));
      pointer-events: auto;
      opacity: 0;
      visibility: hidden;
    }

    #${HINT_ID}.ctc-zoom-controls-hint.is-visible {
      opacity: 1 !important;
      visibility: visible !important;
      pointer-events: auto !important;
      filter: none !important;
      animation: ctcZoomHintEnter 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }

    #${HINT_ID}.ctc-zoom-controls-hint.is-dismissing {
      pointer-events: none !important;
      animation: ctcZoomHintExit 0.38s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    #${HINT_ID}[data-controls-device='desktop'] {
      right: 1.25rem;
      bottom: 1.25rem;
      left: auto;
      top: auto;
      transform: none;
    }

    #${HINT_ID}[data-controls-device='mobile'] {
      top: 4.25rem;
      bottom: auto;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
      text-align: center;
    }

    #${HINT_ID}[data-controls-device='mobile'].is-visible {
      animation-name: ctcZoomHintEnterMobile;
    }

    #${HINT_ID}[data-controls-device='mobile'].is-dismissing {
      animation-name: ctcZoomHintExitMobile;
    }

    #${HINT_ID} .ctc-zoom-controls-hint__glow {
      position: absolute;
      inset: -1px;
      border-radius: 1rem;
      background: linear-gradient(135deg, rgb(110 200 255 / 0.55), rgb(8 74 110 / 0.35), rgb(110 200 255 / 0.25));
      opacity: 0.85;
      animation: ctcZoomHintGlow 3.2s ease-in-out infinite;
      pointer-events: none;
    }

    #${HINT_ID} .ctc-zoom-controls-hint__panel {
      position: relative;
      padding: 1rem 1.1rem 0.95rem;
      border-radius: calc(1rem - 1px);
      background: linear-gradient(155deg, rgb(8 20 34 / 0.94), rgb(4 12 22 / 0.97));
      border: 1px solid rgb(110 200 255 / 0.22);
      box-shadow:
        0 18px 40px rgb(0 0 0 / 0.45),
        0 0 0 1px rgb(255 255 255 / 0.04) inset,
        0 1px 0 rgb(255 255 255 / 0.06) inset;
      color: #eff8ff;
      font-size: 0.8125rem;
      line-height: 1.45;
      backdrop-filter: blur(12px);
    }

    #${HINT_ID} .ctc-zoom-controls-hint__title {
      margin: 0 0 0.65rem;
      font-size: 0.9375rem;
      font-weight: 650;
      letter-spacing: 0.01em;
      color: #eff8ff;
    }

    #${HINT_ID} .ctc-zoom-controls-hint__list {
      margin: 0;
      padding: 0;
      list-style: none;
      text-align: left;
    }

    #${HINT_ID} .ctc-zoom-controls-hint__list[hidden] {
      display: none !important;
    }

    #${HINT_ID} .ctc-zoom-controls-hint__item {
      display: flex;
      align-items: flex-start;
      gap: 0.55rem;
      margin: 0.35rem 0;
    }

    #${HINT_ID}.is-visible .ctc-zoom-controls-hint__item {
      opacity: 1;
    }

    #${HINT_ID} .ctc-zoom-controls-hint__icon {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 0.45rem;
      background: linear-gradient(145deg, rgb(110 200 255 / 0.18), rgb(8 74 110 / 0.35));
      border: 1px solid rgb(110 200 255 / 0.25);
      color: var(--ctc-hint-accent);
      box-shadow: 0 2px 8px rgb(0 0 0 / 0.2);
    }

    #${HINT_ID} .ctc-zoom-controls-hint__icon svg {
      display: block;
    }

    #${HINT_ID} .ctc-zoom-controls-hint__dismiss {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: 0.85rem;
      width: 100%;
      padding: 0.55rem 1rem;
      border: 0;
      border-radius: 999px;
      cursor: pointer;
      font-size: 0.8125rem;
      font-weight: 650;
      letter-spacing: 0.02em;
      color: #f5fbff;
      background: linear-gradient(135deg, #084a6ef2, #072643eb);
      border: 1px solid rgb(135 208 255 / 0.45);
      box-shadow: 0 8px 20px rgb(0 0 0 / 0.35);
    }

    @keyframes ctcZoomHintEnter {
      from { opacity: 0; transform: translateY(0.75rem) scale(0.96); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes ctcZoomHintEnterMobile {
      from { opacity: 0; transform: translateX(-50%) translateY(0.75rem) scale(0.96); }
      to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
    }

    @keyframes ctcZoomHintExit {
      from { opacity: 1; transform: translateY(0) scale(1); }
      to { opacity: 0; transform: translateY(0.5rem) scale(0.97); }
    }

    @keyframes ctcZoomHintExitMobile {
      from { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
      to { opacity: 0; transform: translateX(-50%) translateY(0.5rem) scale(0.97); }
    }

    @keyframes ctcZoomHintGlow {
      0%, 100% { opacity: 0.65; }
      50% { opacity: 1; }
    }

    @media (prefers-reduced-motion: reduce) {
      #${HINT_ID}.ctc-zoom-controls-hint.is-visible,
      #${HINT_ID}.ctc-zoom-controls-hint.is-dismissing {
        animation: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}

/**
 * @param {Element | null | undefined} root
 * @returns {HTMLDialogElement | null}
 */
function resolveOpenDialog(root) {
  if (root instanceof HTMLDialogElement) {
    return root;
  }

  if (root instanceof Element) {
    if (root.localName === 'dialog') {
      return /** @type {HTMLDialogElement} */ (root);
    }

    const nestedDialog = root.querySelector('dialog.dialog-zoomed-gallery__dialog, dialog[open]');
    if (nestedDialog instanceof HTMLDialogElement) {
      return nestedDialog;
    }

    const closestDialog = root.closest('dialog');
    if (closestDialog instanceof HTMLDialogElement) {
      return closestDialog;
    }
  }

  return findOpenZoomDialog();
}

/**
 * @returns {HTMLDialogElement | null}
 */
function findOpenZoomDialog() {
  const openDialog = document.querySelector(
    'dialog.dialog-zoomed-gallery__dialog[open], zoom-dialog dialog[open]'
  );
  return openDialog instanceof HTMLDialogElement ? openDialog : null;
}

/**
 * @param {HTMLDialogElement} dialog
 * @returns {HTMLElement}
 */
function ensureHintLayer(dialog) {
  injectStyles();

  let layer = dialog.querySelector(`:scope > .${LAYER_CLASS}`);
  if (!(layer instanceof HTMLElement)) {
    layer = document.createElement('div');
    layer.className = LAYER_CLASS;
    layer.setAttribute('aria-hidden', 'true');
    dialog.appendChild(layer);
  }

  return layer;
}

/**
 * @returns {HTMLElement}
 */
function buildHintFromTemplate() {
  const template = document.getElementById('zoom-controls-hint-template');
  const seed = template?.content?.firstElementChild;
  if (seed instanceof HTMLElement) {
    const hint = /** @type {HTMLElement} */ (seed.cloneNode(true));
    hint.id = HINT_ID;
    hint.setAttribute('data-ctc-zoom-hint-overlay', '');
    hint.classList.add('ctc-zoom-controls-hint');
    hint.classList.remove('is-visible', 'is-dismissing', 'is-hidden', 'dialog-zoomed-gallery__controls-hint');
    return hint;
  }

  const hint = document.createElement('div');
  hint.id = HINT_ID;
  hint.className = 'ctc-zoom-controls-hint';
  hint.setAttribute('data-ctc-zoom-hint-overlay', '');
  hint.setAttribute('role', 'status');

  const mobile = isMobileViewport();
  hint.innerHTML = `
    <div class="ctc-zoom-controls-hint__glow" aria-hidden="true"></div>
    <div class="ctc-zoom-controls-hint__panel">
      <p class="ctc-zoom-controls-hint__title">Explore the image</p>
      <ul class="dialog-zoomed-gallery__controls-list dialog-zoomed-gallery__controls-list--desktop ctc-zoom-controls-hint__list" ${mobile ? 'hidden' : ''}>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">⊕</span><span>Scroll to zoom in and out</span></li>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">✥</span><span>Click and drag to pan</span></li>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">▣</span><span>Double-click to toggle zoom</span></li>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">×</span><span>Press Esc or × to close</span></li>
      </ul>
      <ul class="dialog-zoomed-gallery__controls-list dialog-zoomed-gallery__controls-list--mobile ctc-zoom-controls-hint__list" ${mobile ? '' : 'hidden'}>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">⊘</span><span>Pinch to zoom in and out</span></li>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">✥</span><span>Drag to pan</span></li>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">▣</span><span>Double-tap to toggle zoom</span></li>
        <li class="ctc-zoom-controls-hint__item"><span class="ctc-zoom-controls-hint__icon" aria-hidden="true">×</span><span>Tap × to close</span></li>
      </ul>
      <button type="button" class="ctc-zoom-controls-hint__dismiss">Got it</button>
    </div>
  `;

  return hint;
}

/**
 * @param {Element | null | undefined} interactionRoot
 * @returns {HTMLElement | null}
 */
function getHintElement(interactionRoot) {
  const dialog = resolveOpenDialog(interactionRoot);
  if (!(dialog instanceof HTMLDialogElement)) return null;

  const layer = ensureHintLayer(dialog);

  let hint = document.getElementById(HINT_ID);
  if (!(hint instanceof HTMLElement)) {
    hint = buildHintFromTemplate();
  }

  if (hint.parentElement !== layer) {
    layer.appendChild(hint);
  }

  return hint;
}

/**
 * @param {HTMLElement} hint
 */
function syncHintDevice(hint) {
  const mobile = isMobileViewport();
  hint.dataset.controlsDevice = mobile ? 'mobile' : 'desktop';

  const desktopList = hint.querySelector('.dialog-zoomed-gallery__controls-list--desktop');
  const mobileList = hint.querySelector('.dialog-zoomed-gallery__controls-list--mobile');

  desktopList?.toggleAttribute('hidden', mobile);
  mobileList?.toggleAttribute('hidden', !mobile);
}

/**
 * @param {HTMLElement} hint
 */
function bindDismiss(hint) {
  if (hint.dataset.ctcDismissBound === 'true') return;

  abortController?.abort();
  abortController = new AbortController();
  const { signal } = abortController;

  const dismissButton = hint.querySelector('.ctc-zoom-controls-hint__dismiss, .dialog-zoomed-gallery__controls-dismiss');
  dismissButton?.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      dismissZoomControlsHintByUser();
    },
    { signal }
  );

  hint.dataset.ctcDismissBound = 'true';
}

/**
 * @param {HTMLElement} hint
 */
function revealHint(hint) {
  syncHintDevice(hint);
  hint.classList.remove('is-hidden', 'is-dismissing');
  hint.classList.add('is-visible');
  hint.style.setProperty('opacity', '1', 'important');
  hint.style.setProperty('visibility', 'visible', 'important');
  hint.style.setProperty('pointer-events', 'auto', 'important');
}

/**
 * @param {Element | null | undefined} [interactionRoot]
 */
export function showZoomControlsHint(interactionRoot) {
  if (dismissedForSession) return;

  const hint = getHintElement(interactionRoot);
  if (!hint) return;

  revealHint(hint);
  bindDismiss(hint);
}

/** Re-assert visibility after scroll, image change, zoom, or pan. */
export function pinZoomControlsHint() {
  if (dismissedForSession) return;
  showZoomControlsHint(findOpenZoomDialog());
}

function dismissZoomControlsHintByUser() {
  dismissedForSession = true;

  const hint = document.getElementById(HINT_ID);
  if (!(hint instanceof HTMLElement)) return;

  hint.classList.remove('is-visible');
  hint.classList.add('is-dismissing');
  hint.dataset.ctcDismissBound = '';

  const finish = () => {
    hint.classList.remove('is-dismissing');
    hint.style.setProperty('opacity', '0', 'important');
    hint.style.setProperty('visibility', 'hidden', 'important');
    hint.style.setProperty('pointer-events', 'none', 'important');
  };

  hint.addEventListener('animationend', finish, { once: true });
  window.setTimeout(finish, 450);
}

export function hideZoomControlsHint() {
  abortController?.abort();
  abortController = null;

  const hint = document.getElementById(HINT_ID);
  if (!(hint instanceof HTMLElement)) return;

  hint.classList.remove('is-visible', 'is-dismissing');
  hint.dataset.ctcDismissBound = '';
  hint.style.removeProperty('opacity');
  hint.style.removeProperty('visibility');
  hint.style.removeProperty('pointer-events');
}

export function resetZoomControlsHintSession() {
  dismissedForSession = false;
  hideZoomControlsHint();
}

function scheduleShow(interactionRoot) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      showZoomControlsHint(interactionRoot);
    });
  });
}

window.ctcShowZoomControlsHint = showZoomControlsHint;
window.ctcScheduleZoomControlsHint = scheduleShow;
window.ctcPinZoomControlsHint = pinZoomControlsHint;
window.ctcHideZoomControlsHint = hideZoomControlsHint;
window.ctcResetZoomControlsHintSession = resetZoomControlsHintSession;
